import {
    LiveKitRoom,
    useTracks,
    VideoTrack,
    useConnectionState,
    useRoomContext,
    RoomAudioRenderer,
} from "@livekit/components-react";
import { Track, ConnectionState } from "livekit-client";
import React, { useContext, useEffect, useState } from "react";
import { fetchLivekitToken } from "../redux/action/liveAuctionAction";
import { useDispatch, useSelector } from "react-redux";
import useTranslationHook from "./hooks/useTranslationHook";

const defaultViewerCount = {
    active_bidder_count: 0,
    bidder_data: 0,
    watcher_count: 0,
};

const ViewerCountContext = React.createContext(defaultViewerCount);

function StatsOverlay() {
    const viewerCount = useContext(ViewerCountContext);
    const { t } = useTranslationHook();
    const {
        active_bidder_count = 0,
        bidder_data = 0,
        watcher_count = 0,
    } = viewerCount || {};

    return (
        <div className="statscontainer">
            <div className="statitem">
                <img src="/img/live-eyes.svg" alt="" /> {watcher_count} {t("Viewers")}
            </div>
            <div className="statitem">
                <img src="/img/live-bid.svg" alt="" /> {bidder_data} {t("Bids")}
            </div>
            <div className="statitem">
                <img src="/img/live-bidder.svg" alt="" /> {active_bidder_count} {t("Bidders")}
            </div>
        </div>
    );
}

function VideoTile({ trackRef, isMain = false }) {
    const [muted, setMuted] = useState(true);

    return (
        <div style={styles.videoContainer}>
            <VideoTrack trackRef={trackRef} style={styles.video} />

            <RoomAudioRenderer trackRef={trackRef} muted={muted} />

            {isMain && (
                <div style={styles.topOverlay}>
                    <div style={styles.liveBadge}>
                        <span style={styles.liveDot}></span>
                        LIVE
                    </div>

                    <button
                        onClick={() => setMuted(!muted)}
                        style={styles.muteButton}
                    >
                        <img src={`/img/${muted ? 'mute' : 'audio-icon'}.svg`} alt="audio toggle" />
                    </button>
                </div>
            )}
        </div>
    );
}


function SpotlightLayout({ tracks }) {
    if (!tracks || tracks.length === 0) return null;

    const screenTrack = tracks.find(
        (t) => t.publication?.source === Track.Source.ScreenShare
    );

    const cameraTrack = tracks.find(
        (t) => t.publication?.source === Track.Source.Camera
    );

    return (
        <div style={styles.spotlightContainer}>
            {screenTrack ? (
                <>
                    <div style={styles.mainView}>
                        <VideoTile trackRef={screenTrack} isMain />
                        <StatsOverlay />
                    </div>

                    {cameraTrack && (
                        <div style={styles.pipView}>
                            <VideoTile trackRef={cameraTrack} />
                        </div>
                    )}
                </>
            ) : (
                cameraTrack && (
                    <div style={styles.mainView}>
                        <VideoTile trackRef={cameraTrack} isMain />
                        <StatsOverlay />
                    </div>
                )
            )}
        </div>
    );
}


function CustomVideoConference({ error, viewerCount }) {
    const connectionState = useConnectionState();
    const room = useRoomContext();

    const tracks = useTracks([
        { source: Track.Source.ScreenShare, withPlaceholder: false },
        { source: Track.Source.Camera, withPlaceholder: true },
    ]);

    const [hasStreamStarted, setHasStreamStarted] = useState(false);

    const isCurrentlyLive = tracks.some(
        (t) =>
            t.publication &&
            t.publication.isSubscribed &&
            t.publication.track
    );

    useEffect(() => {
        if (isCurrentlyLive) {
            setHasStreamStarted(true);
        }
    }, [isCurrentlyLive]);

    if (error) {
        return <div>Error: {error.message}</div>;
    }
    // if (connectionState === ConnectionState.Connecting) {
    //     return <div>Connecting...</div>;
    // }

    // if (connectionState === ConnectionState.Disconnected) {
    //     return <div>Room closed</div>;
    // }

    // if (!room) {
    //     return <div>Room not available</div>;
    // }

    if (!isCurrentlyLive && !hasStreamStarted) {
        return (
            <div className="live-stream">
                <img className="nodata"
                    src="/img/Media.jpeg"
                    alt="Live stream"
                />
            </div>
        );
    }

    if (!isCurrentlyLive && hasStreamStarted) {
        return (
            // <div className="stream-end">
            //     <p style={styles.endedText}>Stream Ended</p>
            // </div>
            <div className="live-stream">
                <img className="nodata"
                    src="/img/Media.jpeg"
                    alt="Live stream"
                />
            </div>
        );
    }

    return <SpotlightLayout tracks={tracks} />;
}

export default function Viewer({ viewerCount, selectedSessionID }) {
    const dispatch = useDispatch()
    const [error, setError] = useState(null);
    const [token, setToken] = useState(null);
    const selectedLot = useSelector((state) => state.liveAuction.selectedLot);
    const session_id = selectedLot?.session_id || selectedSessionID;

    useEffect(() => {
        if (!session_id) return;
        const startViewer = async () => {
            try {
                const response = await dispatch(fetchLivekitToken({ session_id }));

                if (!response?.data) {
                    throw new Error("Invalid LiveKit response");
                }

                const {
                    livekit_token: livekitToken,
                } = response.data;

                if (!livekitToken) {
                    throw new Error("Missing LiveKit credentials");
                }
                setToken(livekitToken)
            } catch (err) {
                console.error("Viewer error:", err);
            }
        };

        startViewer();
    }, [session_id]);

    return (
        <>
            {/* <ViewerCountContext.Provider value={viewerCount || defaultViewerCount}> */}
            <ViewerCountContext.Provider value={viewerCount}>
                <LiveKitRoom
                    id="video_stream_container"
                    serverUrl={process.env.REACT_APP_LIVEKITURL}
                    token={token}
                    connect={true}
                    video={false}
                    audio={false}
                    onError={(err) => setError(err)}
                    onConnected={() => setError(null)}
                    style={{ width: "100%" }}
                >
                    <CustomVideoConference error={error} />
                </LiveKitRoom>
            </ViewerCountContext.Provider>
        </>
    );
}


const styles = {
    grid: {
        display: "grid",
        // gap: "10px",
        // padding: "10px",
        height: "400px"
    },
    tile: {
        background: "#000",
        borderRadius: "10px",
        overflow: "hidden",
    },
    video: {
        width: "100%",
        height: "80%",
        objectFit: "cover",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "10px",
    },
    videoContainer: {
        position: "relative",
        width: "100%",
        height: "100%",
    },
    video: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    overlay: {
        position: "absolute",
        bottom: "10px",
        left: "10px",
        right: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    liveBadge: {
        display: "flex",
        alignItems: "center",
        color: "#fff",
        fontSize: "14px",
        fontWeight: "500",
    },
    liveDot: {
        width: "12px",
        height: "12px",
        backgroundColor: "#EB5757",
        borderRadius: "50%",
        marginRight: "6px",
    },
    muteButton: {
        color: "#fff",
        cursor: "pointer",
        fontSize: "16px",
    },

    spotlightContainer: {
        position: "relative",
        width: "100%",
        height: "50vh",
        overflow: "hidden",
    },

    mainView: {
        width: "100%",
        height: "100%",
    },

    pipView: {
        position: "absolute",
        bottom: "20px",
        right: "20px",
        width: "260px",
        height: "150px",
        borderRadius: "12px",
        overflow: "hidden",
        border: "2px solid white",
        boxShadow: "0 4px 12px hsla(144, 96%, 49%, 0.40)",
        zIndex: 10,
    },
    topOverlay: {
        position: "absolute",
        bottom: "12px",
        left: "12px",
        right: "12px",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "24px",
        zIndex: 20,
    },

    overlay: {
        position: "absolute",
        top: "10px",
        left: "10px",
        right: "10px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },




};



// const Viewer = () => {
//     return (
//         <div style={{ position: "relative" }}>
//             <img
//                 src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c"
//                 style={styles.video}
//                 alt=""
//             />

//             <div style={styles.live}>● LIVE</div>

//             {/* Overlay speaker video */}
//             <div style={styles.overlayVideo}>
//                 <img
//                     src="https://images.unsplash.com/photo-1544005313-94ddf0286df2"
//                     style={styles.overlayImg}
//                     alt=""
//                 />
//             </div>
//         </div>
//     )
// }

// const styles = {
//     video: {
//         width: "100%",
//         height: 420,
//         borderRadius: 16,
//         objectFit: "cover",
//     },

//     overlayVideo: {
//         position: "absolute",
//         bottom: 20,
//         right: 20,
//         width: 180,
//     },

//     overlayImg: { width: "100%", borderRadius: 10 },

//     live: {
//         position: "absolute",
//         bottom: 20,
//         left: 20,
//         background: "red",
//         color: "#fff",
//         padding: "5px 10px",
//         borderRadius: 6,
//     }
// };